import { Table } from "@tanstack/react-table";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { UseFormWatch } from "react-hook-form";
import { useLocation } from "react-router-dom";

import { InvoicingServiceContextProvider } from "./InvoiceServiceProvider";

import {
  InvoicingLocalKeys,
  InvoicingQueryParams,
} from "@/constants/invoicing.constants";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import DataTableSlotContextProvider from "@/context/DataTableSlot";
import useQueryParams from "@/hooks/useQueryParams";
import { ContractInvoiceItem } from "@/interfaces/invoicing.interface";
import { Docket } from "@/interfaces/workdocket.interface";
import { TrackingItem } from "@/interfaces/xero.interface";
import { useXeroTrackingItemsQuery } from "@/redux/api/xeroQuery";
import { CreateDocketInvoicing } from "@/schemas/invoicing.schema";
import { getSelectedTableRows } from "@/utils/table-utils";

type InvoicingAction = {
  for: string;
  action: string;
};

export type filterItem = {
  value: string;
  label: string;
};

type UtilsWatch = {
  watch: UseFormWatch<CreateDocketInvoicing>;
};

type InvoicingContextType<T> = {
  selectedCustomerId?: string;
  isShowFloatingMenu: boolean;
  selectedItems: string[];
  tables: { [key: string]: Table<T> };
  isSheetOpen: boolean;
  handleAddTable: (key: string, table: Table<T>) => void;
  setToggle: () => void;
  toggleSheet: () => void;
  resetAllSelected: () => void;
  handleAddToLocalStorage: (value: InvoicingAction) => void;
  getLocalStorageData: () => InvoicingAction | null;
  removeLocalStorage: () => void;
  xeroTrackingItems: TrackingItem[] | undefined;
  isTrackingItemLoading: boolean;
  trackingNameOptions: filterItem[];
  isInvoicingForDocket: boolean;

  calculateTotal: ({ watch }: UtilsWatch) => number;
  calculateSubtotal: ({ watch }: UtilsWatch) => number;
  calculateTax: ({ watch }: UtilsWatch) => string;
  calculateDiscount: ({ watch }: UtilsWatch) => number;
  calculateTotalTaxAmount: ({ watch }: UtilsWatch) => number;
};

type InvoicingContextProviderProps = {
  children: ReactNode;
};

type ReturnTypes<T> = {
  InvoicingContextProvider: ({
    children,
  }: InvoicingContextProviderProps) => JSX.Element;
  useInvoicingContext: () => InvoicingContextType<T>;
};

// Generic context factory function
const createInvoicingContext = <T,>(): ReturnTypes<T> => {
  const InvoicingContext = createContext<InvoicingContextType<T> | undefined>(
    undefined,
  );

  const InvoicingContextProvider = ({
    children,
  }: InvoicingContextProviderProps): JSX.Element => {
    const location = useLocation();
    const isForDockets = location.pathname.includes(ROUTES.DOCKET_INVOICE);
    const { clearParam } = useQueryParams();
    const [selectedCustomerId, setSelectedCustomerId] = useState<
      string | undefined
    >();
    const [isShowFloatingMenu, setIsShowFloatingMenu] = useState(false);
    const [tables, setTables] = useState<{ [key: string]: Table<T> }>({});
    const [selectedItems, setSelectedItems] = useState<string[]>([]);
    const [toggle, setToggle] = useState(false);
    const [isSheetOpen, setIsSheetOpen] = useState(false);
    const [invoicingAction, setInvoicingAction] =
      useState<InvoicingAction | null>(() =>
        JSON.parse(
          localStorage.getItem(InvoicingLocalKeys.INVOICING_ACTIONS) || "null",
        ),
      );
    const { data, isLoading } = useXeroTrackingItemsQuery(
      {
        filter: "",
      },
      {
        skip: !isSheetOpen,
      },
    );

    const handleAddTable = (key: string, table: Table<T>): void => {
      if (!tables[key]) {
        setTables((prev) => ({ ...prev, [key]: table }));
      } else {
        setTables((prev) => ({ ...prev, [key]: table }));
      }
    };

    const handleAddToLocalStorage = useCallback(
      (value: InvoicingAction): void => {
        localStorage.setItem(
          InvoicingLocalKeys.INVOICING_ACTIONS,
          JSON.stringify(value),
        );
        setInvoicingAction(value); // Update local state immediately
      },
      [
        localStorage.getItem(InvoicingLocalKeys.INVOICING_ACTIONS),
        setInvoicingAction,
      ],
    );

    const getLocalStorageData = useCallback(
      (): InvoicingAction | null => invoicingAction,
      [invoicingAction, handleAddToLocalStorage],
    );

    const removeLocalStorage = (): void => {
      localStorage.removeItem(InvoicingLocalKeys.INVOICING_ACTIONS);
      setInvoicingAction(null);
    };

    const invoicingActions = getLocalStorageData();
    const isInvoicingForDocket =
      invoicingActions?.for === InvoicingLocalKeys.DOCKET;

    const getAllSelectedIds = (): {
      selectedIds: string[];
      selectedCustomerIds: string[];
    } => {
      const selectedIds: string[] = [];
      const selectedCustomerIds: string[] = [];
      Object.values(tables).forEach((table) => {
        selectedIds.push(...getSelectedTableRows(table));
        selectedCustomerIds.push(
          ...table.getSelectedRowModel().flatRows.map(
            (row) =>
              //@ts-expect-error - customerId is not in the type
              row.original.customerId ?? row.original.customer?.id,
          ),
        );
      });

      return { selectedIds, selectedCustomerIds };
    };

    const resetAllSelected = (): void => {
      Object.values(tables).forEach((table) => {
        table.toggleAllRowsSelected(false);
      });
      setToggle((prev) => !prev);
    };

    const toggleSheet = (): void => {
      setIsSheetOpen((prev) => !prev);
      // Reset the query params when the sheet is closed
      // reset();
      clearParam(
        isForDockets
          ? InvoicingQueryParams.DOCKET_ID
          : InvoicingQueryParams.CONTRACT_ID,
      );
      clearParam(InvoicingQueryParams.CUSTOMER_ID);
      //Reset all selected items when the sheet is closed
      resetAllSelected();
    };

    const handleFloatingMenus = (customerIds: string[]): void => {
      const allSameCustomerIds = customerIds.every(
        (customerId) => customerId === customerIds[0],
      );

      if (allSameCustomerIds && customerIds.length) {
        setSelectedCustomerId(customerIds[0]);
        setIsShowFloatingMenu(true);
      }

      if (!customerIds.length) {
        setIsShowFloatingMenu(false);
        setSelectedCustomerId(undefined);
      }

      if (!allSameCustomerIds) {
        setIsShowFloatingMenu(false);
        setSelectedCustomerId(undefined);
      }
    };

    const trackingNameOptions =
      data?.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? [];

    // for calculating utils
    const calculateTotal = ({ watch }: UtilsWatch): number => {
      const total = watch("itemSheets")?.reduce((acc, item) => {
        const price = Number(item?.price) || 0;
        const quantity = Number(item?.quantity) || 0;
        const discount = Number(item?.discount) || 0; // Discount is a fixed amount
        const tax = Number(item?.tax) || 0; // Tax is a percentage

        // if (price <= 0 || quantity <= 0) {
        //   return acc;
        // }

        const subtotal = price * quantity;
        const subtotalAfterDiscount = subtotal - discount;

        const totalWithTax =
          subtotalAfterDiscount + (subtotalAfterDiscount * tax) / 100;

        return acc + totalWithTax;
      }, 0);

      return total;
    };

    const calculateSubtotal = ({ watch }: UtilsWatch): number => {
      const subtotal = watch("itemSheets")?.reduce((acc, item) => {
        const price = Number(item?.price) || 0;
        const quantity = Number(item?.quantity) || 0;

        return acc + price * quantity;
      }, 0);

      return subtotal;
    };

    const calculateTax = ({ watch }: UtilsWatch): string => {
      const totalTax =
        watch("itemSheets")?.reduce(
          (acc, item) => acc + (Number(item?.tax) || 0),
          0,
        ) || 0;

      return totalTax.toString().replace(/^0+/, "") || "0";
    };

    const calculateDiscount = ({ watch }: UtilsWatch): number => {
      return (
        watch("itemSheets")?.reduce(
          (acc, item) => acc + (Number(item?.discount) || 0),
          0,
        ) || 0
      );
    };

    const calculateTotalTaxAmount = ({ watch }: UtilsWatch): number => {
      const totalTax =
        watch("itemSheets")?.reduce(
          (acc, item) =>
            acc +
            ((Number(item?.price) || 0) *
              (Number(item?.tax) || 0) *
              (Number(item?.quantity) || 0)) /
              100,
          0,
        ) || 0;

      return totalTax;
    };

    useEffect(() => {
      const { selectedIds, selectedCustomerIds } = getAllSelectedIds();
      handleFloatingMenus(selectedCustomerIds);
      setSelectedItems(selectedIds);
    }, [toggle]);

    useEffect(() => {
      const handleStorageChange = (event: StorageEvent): void => {
        if (event.key === InvoicingLocalKeys.INVOICING_ACTIONS) {
          setInvoicingAction(
            event.newValue ? JSON.parse(event.newValue) : null,
          );
        }
      };

      window.addEventListener("storage", handleStorageChange);

      return (): void => {
        window.removeEventListener("storage", handleStorageChange);
      };
    }, []);

    return (
      <InvoicingServiceContextProvider>
        <InvoicingContext.Provider
          value={{
            selectedCustomerId,
            isShowFloatingMenu,
            selectedItems,
            tables,
            isSheetOpen,
            handleAddTable,
            setToggle: () => setToggle((prev) => !prev),
            toggleSheet,
            resetAllSelected,
            getLocalStorageData,
            handleAddToLocalStorage,
            removeLocalStorage,
            isTrackingItemLoading: isLoading,
            xeroTrackingItems: data,
            trackingNameOptions,
            isInvoicingForDocket,
            calculateTotal,
            calculateSubtotal,
            calculateTax,
            calculateDiscount,
            calculateTotalTaxAmount,
          }}
        >
          <DataTableSlotContextProvider>
            {children}
          </DataTableSlotContextProvider>
        </InvoicingContext.Provider>
      </InvoicingServiceContextProvider>
    );
  };

  const useInvoicingContext = (): InvoicingContextType<T> => {
    const context = useContext(InvoicingContext);
    if (context === undefined) {
      throw new Error(
        "useInvoicingContext must be used within an InvoicingContextProvider",
      );
    }

    return context;
  };

  return { InvoicingContextProvider, useInvoicingContext };
};

const { InvoicingContextProvider, useInvoicingContext } =
  createInvoicingContext<Docket | ContractInvoiceItem>();

export {
  createInvoicingContext,
  InvoicingContextProvider,
  useInvoicingContext,
};
